import { useRouter } from '@tanstack/react-router'
import type { CommandPaletteTarget } from 'features/commandPalette/utils/commandPaletteTargets'
import { escapeForRolodexIlike } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/browseAllFilters'
import { useActivePipelineConfigId } from 'features/knowledgeDocumentViewer/hooks/useActivePipelineConfigId'
import { useRolodexEntries, useRolodexHistogram } from 'features/rolodex'
import { FileText } from 'lucide-react'
import { useMemo } from 'react'
import { SEARCH_PARAM_SITE_ID } from 'routes/(global_search_params)/-searchParams'
import { CLEAR_SEARCH_VIEWER_PARAMS } from 'routes/documents/-searchParams'
import {
  type GeckoEntry,
  RolodexEntryType,
  RolodexFilterOp,
  type RolodexHistogramQuery,
} from 'types'
import { useDebouncedValue } from 'utils/hookHelpers'

export const DOCUMENT_KIND_LABEL = 'Documents'
const DOCUMENT_RESULT_LIMIT = 30
const DOCUMENT_DEBOUNCE_MS = 150

/** Same fallback chain as the documents table title, so the row and the page agree. */
const TITLE_FIELDS = ['document_title', 'drawing_number', 'title'] as const

const stringField = (entry: GeckoEntry, key: string): string | undefined => {
  const value = entry.data[key]
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

const documentLabel = (entry: GeckoEntry): string | undefined =>
  TITLE_FIELDS.map((key) => stringField(entry, key)).find(Boolean)

export interface UseCommandPaletteDocumentTargetsResult {
  targets: CommandPaletteTarget[]
  isSearching: boolean
}

/** Documents in the site's active pipeline scope whose title matches the query. */
export function useCommandPaletteDocumentTargets({
  query,
  siteId,
  enabled,
}: {
  query: string
  siteId: string | undefined
  enabled: boolean
}): UseCommandPaletteDocumentTargetsResult {
  const router = useRouter()
  const trimmedQuery = enabled ? query.trim() : ''
  const { value: debouncedQuery, isDebouncing } = useDebouncedValue(
    trimmedQuery,
    DOCUMENT_DEBOUNCE_MS
  )
  const { activePipelineConfigId } = useActivePipelineConfigId(
    enabled && trimmedQuery ? siteId : undefined
  )

  const idsQuery = useMemo<RolodexHistogramQuery | undefined>(() => {
    if (!debouncedQuery || !siteId || !activePipelineConfigId) return undefined
    const pattern = escapeForRolodexIlike(debouncedQuery)
    return {
      type: RolodexEntryType.SYSTEM_DOCUMENT,
      tags: { site: siteId, 'pipeline-config': activePipelineConfigId },
      advanced_filter: {
        operator: 'or',
        filters: TITLE_FIELDS.map((key) => ({
          field: 'data',
          key,
          op: RolodexFilterOp.REGEX,
          value: pattern,
        })),
      },
      select: [{ field: 'id', label: 'id' }],
      limit: DOCUMENT_RESULT_LIMIT,
    }
  }, [activePipelineConfigId, debouncedQuery, siteId])

  const { data: idRows, isLoading: isLoadingIds } =
    useRolodexHistogram(idsQuery)

  const ids = useMemo(
    () =>
      (idRows ?? [])
        .flat()
        .map((row) => row.id)
        .filter((id): id is string => typeof id === 'string'),
    [idRows]
  )

  const { entries, isLoading: isLoadingEntries } = useRolodexEntries(
    ids.length > 0
      ? { ids, type: [RolodexEntryType.SYSTEM_DOCUMENT] }
      : undefined
  )

  const targets = useMemo<CommandPaletteTarget[]>(() => {
    if (!siteId || !debouncedQuery) return []
    return entries.flatMap((entry) => {
      const label = documentLabel(entry)
      if (!label) return []
      const documentId = stringField(entry, 'document_id') ?? entry.id
      return [
        {
          id: `document:${documentId}`,
          kind: 'document' as const,
          label,
          kindLabel: DOCUMENT_KIND_LABEL,
          icon: FileText,
          documentId,
          href: router.buildLocation({
            to: '/documents',
            search: {
              ...CLEAR_SEARCH_VIEWER_PARAMS,
              [SEARCH_PARAM_SITE_ID]: siteId,
              documentID: documentId,
            },
          }).href,
        },
      ]
    })
  }, [debouncedQuery, entries, router, siteId])

  return {
    targets,
    isSearching:
      trimmedQuery.length > 0 &&
      (isDebouncing || isLoadingIds || isLoadingEntries),
  }
}
